// CurrencyPresentationProvider — the operator's configured currency
// presentation, made available to every figure in the console.
//
// SM-443 designed `resolveCurrencyPresentation(code, settings)` to take
// operator configuration, and SM-444/SM-485 shipped the page that writes it:
// /currencies sets each currency's display name, symbol, colour, display
// precision and artwork, PUTs to backoffice-service → ledger, and audits the
// change twice. Nothing read it. `settings` was never once passed outside the
// settings page itself, so every balance in the console rendered from the
// frozen seed and an uploaded logo appeared nowhere.
//
// This is the missing half: fetch the registry once per session, hand it down,
// and let <CurrencyAmount> resolve against it. Configuration that cannot be
// seen is not configuration.
//
// THREE PROPERTIES THIS MUST KEEP:
//
//  1. It never blocks or breaks a render. `resolveCurrencyPresentation` is
//     total — it fills every field from the seed — so "no settings yet" is a
//     complete, correct presentation and not a loading state. Figures paint on
//     first frame with the seed and re-paint if configuration differs.
//
//  2. It never asks for what the operator may not read. The registry needs
//     CURRENCY_VIEW; an operator without it gets the seed rather than a 403 in
//     the console for every page they open.
//
//  3. A failed read is not an error. Presentation degrading to the seed is
//     invisible and harmless; a thrown error on a treasury page because a
//     cosmetic fetch failed is not. The /currencies page still surfaces its own
//     read failure loudly — that page is ABOUT the registry.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { currencyKeys, fetchCurrencyRegistry } from "@/lib/currency-queries";
import type { CurrencyInfo } from "@/lib/currency-types";
import { useMyPerms } from "@/hooks/useMyPerms";
import { campaignCurrency } from "@/lib/currency";
import { Perm } from "@/lib/perms";
import {
  isCurrencyCode,
  resolveCurrencyPresentation,
  type CurrencyCode,
  type CurrencyPresentation,
  type CurrencyPresentationSettings,
} from "@/currency";

/**
 * Exported so a caller can supply settings from somewhere other than the
 * registry query — tests, and any future surface that already holds the rows
 * (the /currencies page itself renders previews while an edit is still a
 * draft). The provider below is the normal way in.
 */
export const CurrencySettingsContext = createContext<CurrencyPresentationSettings | undefined>(
  undefined,
);

/**
 * Registry rows → the contract's settings shape.
 *
 * Every field is passed through as-is and left for `resolve` to validate: it
 * already rejects a blank name, an unsafe colour, a `javascript:` image src and
 * a nonsense precision, each independently, so a row with a good name and a
 * junk colour keeps its name. Duplicating any of that here would give the
 * console a second opinion about what valid configuration is.
 *
 * An unknown code is skipped rather than coerced. The registry is supposed to
 * hold exactly RED/GREEN/GOLD; a fourth row is a bug somewhere upstream, and
 * quietly drawing it as GOLD would put a wrong mark beside a real balance.
 */
export function toPresentationSettings(
  rows: readonly CurrencyInfo[] | undefined,
): CurrencyPresentationSettings | undefined {
  if (!rows?.length) return undefined;
  const out: CurrencyPresentationSettings = {};
  for (const row of rows) {
    if (!isCurrencyCode(row.code)) continue;
    out[row.code] = {
      displayName: row.display_name,
      symbol: row.symbol,
      color: row.color,
      decimals: row.display_precision,
      // Only claim an image when one is configured. Passing
      // `{ kind: "image", src: undefined }` would resolve to the seed anyway,
      // but saying nothing is the honest encoding of "not configured".
      ...(row.image_url
        ? { icon: { kind: "image" as const, src: row.image_url, alt: row.display_name } }
        : {}),
    };
  }
  return out;
}

/**
 * The registry rows themselves, on the provider's terms: same key, same
 * permission gate, same "a failed read is not an error" posture.
 *
 * Split out of the provider because two different questions are asked of one
 * read. Presentation asks "what does GOLD look like" and is answered by the
 * seed when nothing is configured; a PICKER asks "which currencies are there,
 * and in what order" — a question the seed can only answer generically, and
 * one that needs `sort_order`, which the settings shape drops. Both go through
 * this hook so the console still issues one registry request per session
 * (TanStack dedupes on the shared key) rather than one per asker.
 *
 * Returns undefined while loading, when the operator may not read the registry,
 * and when the read failed — three states a caller must treat the same way:
 * fall back to what the contract knows without showing anyone an error.
 */
export function useCurrencyRegistryRows(): CurrencyInfo[] | undefined {
  const perms = useMyPerms();
  // Before permissions load we do not know, and asking would 403 for an
  // operator who lacks CURRENCY_VIEW. Waiting one tick costs nothing: the seed
  // is already a complete presentation.
  const enabled = perms.loaded && perms.has(Perm.CurrencyView);

  const { data } = useQuery({
    queryKey: currencyKeys.registry(),
    queryFn: fetchCurrencyRegistry,
    enabled,
    // Presentation changes when an operator saves the settings page, which
    // invalidates this key directly. Nothing else moves it, so a long stale
    // time keeps this to roughly one request per session.
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data;
}

export function CurrencyPresentationProvider({ children }: { children: ReactNode }) {
  const data = useCurrencyRegistryRows();

  const settings = useMemo(() => toPresentationSettings(data), [data]);

  return (
    <CurrencySettingsContext.Provider value={settings}>{children}</CurrencySettingsContext.Provider>
  );
}

/** The raw configured settings, for callers that resolve several codes at once. */
export function useCurrencySettings(): CurrencyPresentationSettings | undefined {
  return useContext(CurrencySettingsContext);
}

/**
 * One currency's fully-resolved presentation — configuration where an operator
 * set it, seed everywhere else. Outside a provider this is the seed, which is
 * why a component using it renders correctly in a test with no wrapper.
 */
export function useCurrencyPresentation(code: CurrencyCode): CurrencyPresentation {
  const settings = useCurrencySettings();
  return useMemo(() => resolveCurrencyPresentation(code, settings), [code, settings]);
}

/**
 * A currency's configured display NAME (SM-491), for the slots that name a
 * currency in words rather than draw it beside a figure — a KPI sub-label, a
 * "Currency" field, a sentence.
 *
 * Takes a RAW WIRE CODE, so it is a drop-in for `currencyLabel()`, which
 * answered the same question with a hardcoded "GOLD" and so kept saying that
 * after an operator renamed the currency. An absent code, "CREDIT" and "SPARK"
 * are all the legacy single currency (G3, presentation boundary only).
 */
export function useCurrencyName(currency: string | undefined | null): string {
  return useCurrencyPresentation(campaignCurrency(currency)).displayName;
}
